import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ButtonsGroup from "../../components/ButtonsGroup/ButtonsGroup.jsx";
import Button from "../../components/Button/Button.jsx";

import WrapperContainer from "../../components/WrapperContainer/WrapperContainer.jsx";
import USelect from "../../components/Select/USelect.jsx";

import { APP_ROUTES } from "../../router";
import actions from "../../store/actions.js";

import { useAddress } from "../../hooks/useAddress.js";
import handleChangeFullName from "../../utils/helpers/handleChangeFullName";

import handleChangePhoneNumber from "../../utils/helpers/handleChangePhoneNumber.js";
import checkPhoneNumberLength from "../../utils/helpers/checkPhoneNumberLength.js";

import Input from "../../components/Input/Input.jsx";
import CityListContext from "../../context/cityList";

import Checkbox from "../../components/Checkbox/Checkbox.jsx";
import { OFFER_DEFAULT, AGREEMENT, PRIVATE_DATA_POLICY, ADS } from "../../utils/consts/index.js";

const PersonalDataBusiness = () => {
  const dispatch = useDispatch();
  const history = useNavigate();

  const { mobileConnectionInfo } = useSelector(state => state);
  const cityList = useContext(CityListContext);

  const {
    userCity,
    setUserCity,
    userStreet,
    setUserStreet,
    userHouse,
    setUserHouse,
    changeCity,
    changeStreet,
    changeHouse,
    debouncedLoadStreetList,
    debouncedLoadHouseList,
  } = useAddress();

  const [userFullName, setUserFullName] = useState("");
  const [fullNameErrorText, setFullNameErrorText] = useState([]);

  const [userPhoneNumber, setUserPhoneNumber] = useState("");
  const [userPhoneNumberErrorText, setUserPhoneNumberErrorText] = useState([]);

  const [userFullAddress, setUserFullAddress] = useState("");
  const [isVisibleFullAddressField, setIsVisibleFullAddressField] = useState(false);

  const [agreements, setAgreements] = useState({
    offerta: false,
    policy: false,
    ads: false,
  });

  useEffect(() => {
    getUserInfoFromLocaleStorage();
  }, []);

  const getUserInfoFromLocaleStorage = () => {
    const { city, street, house, fullAddress, fullName, phoneNumber } = mobileConnectionInfo;

    setUserCity(city || userCity);
    setUserStreet(street || userStreet);

    setUserHouse(house || userHouse);
    setUserFullAddress(fullAddress || userFullAddress);

    setUserFullName(fullName || userFullName);
    setUserPhoneNumber(phoneNumber || userPhoneNumber);
  };

  const saveUserInfoToLocaleStorage = async () => {
    dispatch(
      actions.mobileConnection.changeConnectionData({
        ...mobileConnectionInfo,
        refUserId: mobileConnectionInfo.refUserId || "-",
        city: userCity,
        street: userStreet,
        house: userHouse,
        fullName: userFullName.trim(),
        fullAddress: userFullAddress.trim(),
        phoneNumber: userPhoneNumber,
      })
    );
  };

  const redirectToNextStep = async () => {
    const address = userFullAddress.trim();
    const isDataReferenceBook = !isVisibleFullAddressField || !address;

    await saveUserInfoToLocaleStorage();
    history(APP_ROUTES.MOBILE_CONNECTION.CHOICE_SERVICES_BUSINESS_ROUTE, {
      state: {
        isDataReferenceBook: isDataReferenceBook,
      },
    });
  };

  const isFillPhoneNumber = checkPhoneNumberLength(userPhoneNumber);
  const isFillRequiredFields =
    ((userCity && userStreet && userHouse) || userFullAddress) &&
    userFullName &&
    agreements.policy &&
    isFillPhoneNumber;

  return (
    <>
      <WrapperContainer title="sm" gap="16">
        Уфанет для Бизнеса
      </WrapperContainer>
      <WrapperContainer gap="24">Укажите Ваш адрес и контактные данные</WrapperContainer>

      <WrapperContainer gap="16">
        <USelect
          isSearchable
          isClearable
          isDisabled={isVisibleFullAddressField}
          options={cityList}
          getOptionValue={option => option.alias}
          getOptionLabel={option => option.title}
          placeholder="Город"
          onChange={changeCity}
          value={userCity}
        />
      </WrapperContainer>

      <WrapperContainer gap="16">
        <USelect
          isAsync
          isSearchable
          isClearable
          isDisabled={isVisibleFullAddressField}
          options={debouncedLoadStreetList}
          getOptionValue={option => option.id}
          getOptionLabel={option => option.title}
          placeholder="Улица"
          onChange={changeStreet}
          value={userStreet}
        />
      </WrapperContainer>

      <WrapperContainer gap="4">
        <USelect
          isAsync
          isSearchable
          isClearable
          isDisabled={isVisibleFullAddressField}
          options={debouncedLoadHouseList}
          getOptionValue={option => option.identifire}
          getOptionLabel={option => `${option.house}${option.frac}`}
          placeholder="Дом/корпус"
          onChange={changeHouse}
          value={userHouse}
        />
      </WrapperContainer>

      <WrapperContainer gap="16" text="md">
        <div
          className="link clickable"
          onClick={() => setIsVisibleFullAddressField(prevState => !prevState)}
        >
          {!isVisibleFullAddressField ? "Моего адреса нет в списке" : "Выбрать адрес из списка"}
        </div>
      </WrapperContainer>

      {isVisibleFullAddressField && (
        <>
          <WrapperContainer gap="4">
            <Input
              placeholder="Адрес"
              onChange={event => setUserFullAddress(event.target.value)}
              value={userFullAddress}
            />
          </WrapperContainer>

          <WrapperContainer gap="16" text="md">
            <div className="text-color-dark-gray">Например: г. Уфа, ул. пр-кт Октября, д.4/3</div>
          </WrapperContainer>
        </>
      )}

      <WrapperContainer gap="16">
        <Input
          placeholder="ФИО"
          onChange={handleChangeFullName(setFullNameErrorText, setUserFullName)}
          value={userFullName}
          validationErrors={fullNameErrorText}
        />
      </WrapperContainer>

      <WrapperContainer gap="16">
        <Input
          type="tel"
          placeholder="Номер телефона"
          onChange={handleChangePhoneNumber(setUserPhoneNumberErrorText, setUserPhoneNumber)}
          value={userPhoneNumber}
          validationErrors={userPhoneNumberErrorText}
        />
      </WrapperContainer>

      <WrapperContainer gap="24">
        <WrapperContainer gap="16">
          <Checkbox
            onChange={() =>
              setAgreements(prevState => ({
                ...prevState,
                offerta: !prevState.offerta,
              }))
            }
            checked={agreements.offerta}
          >
            <div>
              Я соглашаюсь с условиями{" "}
              <a className="link" href={OFFER_DEFAULT} target="_blank" rel="noreferrer">
                оферты
              </a>
            </div>
          </Checkbox>
        </WrapperContainer>

        <WrapperContainer gap="16">
          <Checkbox
            onChange={() =>
              setAgreements(prevState => ({
                ...prevState,
                policy: !prevState.policy,
              }))
            }
            checked={agreements.policy}
          >
            <div>
              Я даю{" "}
              <a className="link" href={AGREEMENT} target="_blank" rel="noreferrer">
                согласие на обработку моих персональных данных
              </a>{" "}
              в соответствии с{" "}
              <a className="link" href={PRIVATE_DATA_POLICY} target="_blank" rel="noreferrer">
                политикой в отношении обработки персональных данных
              </a>
            </div>
          </Checkbox>
        </WrapperContainer>

        <WrapperContainer gap="16">
          <Checkbox
            onChange={() =>
              setAgreements(prevState => ({
                ...prevState,
                ads: !prevState.ads,
              }))
            }
            checked={agreements.ads}
          >
            <div>
              Я даю{" "}
              <a className="link" href={ADS} target="_blank" rel="noreferrer">
                согласие на получение сообщений рекламно-информационного характера
              </a>
            </div>
          </Checkbox>
        </WrapperContainer>
      </WrapperContainer>

      <ButtonsGroup>
        <Button isStretch size="xxl" color="secondary" onClick={() => history(-1)}>
          Назад
        </Button>
        <Button
          isStretch
          size="xxl"
          color="primary"
          onClick={redirectToNextStep}
          disabled={!isFillRequiredFields}
        >
          Далее
        </Button>
      </ButtonsGroup>
    </>
  );
};

export default PersonalDataBusiness;
